import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'motion/react';
import { Search, Video, Users, Hash, Eye } from 'lucide-react';
import { UserAvatar } from '@/components/ui';
import { http } from '@/network/http';

type SearchTab = 'all' | 'videos' | 'users' | 'hashtags';

interface SearchVideoResult {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  creatorName: string;
  viewCount: number;
}

interface SearchUserResult {
  id: string;
  displayName: string;
  handle: string;
  avatarUrl: string | null;
  followerCount: number;
}

interface SearchHashtagResult {
  id: string;
  name: string;
  videoCount: number;
}

interface SearchResults {
  videos: SearchVideoResult[];
  users: SearchUserResult[];
  hashtags: SearchHashtagResult[];
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

export function SearchPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<SearchTab>('all');
  const debouncedQuery = useDebounce(query, 300);

  const { data, isLoading } = useQuery<SearchResults>({
    queryKey: ['search', debouncedQuery, activeTab],
    queryFn: async () => {
      const res = await http.get('/search', {
        params: { q: debouncedQuery, type: activeTab },
      });
      return res.data;
    },
    enabled: debouncedQuery.length > 0,
  });

  const tabs: { key: SearchTab; label: string; icon: React.ReactNode }[] = [
    { key: 'all', label: t('search.tabAll'), icon: <Search className="h-4 w-4" /> },
    { key: 'videos', label: t('search.tabVideos'), icon: <Video className="h-4 w-4" /> },
    { key: 'users', label: t('search.tabUsers'), icon: <Users className="h-4 w-4" /> },
    { key: 'hashtags', label: t('search.tabHashtags'), icon: <Hash className="h-4 w-4" /> },
  ];

  const hasResults =
    (data?.videos?.length ?? 0) > 0 ||
    (data?.users?.length ?? 0) > 0 ||
    (data?.hashtags?.length ?? 0) > 0;

  const showVideos = activeTab === 'all' || activeTab === 'videos';
  const showUsers = activeTab === 'all' || activeTab === 'users';
  const showHashtags = activeTab === 'all' || activeTab === 'hashtags';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-5 px-4 sm:px-6 md:px-8 max-w-4xl mx-auto"
    >
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('search.placeholder')}
          className="w-full rounded-xl py-3 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          style={{
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(148,163,184,0.2)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg p-1 bg-slate-100/80">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Results */}
      <AnimatePresence mode="wait">
        {!debouncedQuery ? (
          <motion.div
            key="idle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center py-16 text-center"
          >
            <Search className="h-10 w-10 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">{t('search.startTyping')}</p>
          </motion.div>
        ) : isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center justify-center py-16"
          >
            <p className="text-slate-500">{t('common.loading')}</p>
          </motion.div>
        ) : !hasResults ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center py-16 text-center"
          >
            <Search className="h-10 w-10 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">{t('search.noResults')}</p>
          </motion.div>
        ) : (
          <motion.div
            key="results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            {/* Video results */}
            {showVideos && (data?.videos?.length ?? 0) > 0 && (
              <section>
                {activeTab === 'all' && (
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    {t('search.tabVideos')}
                  </h3>
                )}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {data!.videos.map((video) => (
                    <div
                      key={video.id}
                      className="rounded-xl overflow-hidden transition-shadow hover:shadow-lg"
                      style={{
                        background: 'rgba(255,255,255,0.7)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(148,163,184,0.2)',
                      }}
                    >
                      {video.thumbnailUrl ? (
                        <img
                          src={video.thumbnailUrl}
                          alt={video.title}
                          className="aspect-video w-full object-cover"
                        />
                      ) : (
                        <div className="aspect-video w-full bg-slate-100 flex items-center justify-center">
                          <Video className="h-8 w-8 text-slate-300" />
                        </div>
                      )}
                      <div className="p-3">
                        <h4 className="text-sm font-semibold text-slate-900 truncate">
                          {video.title}
                        </h4>
                        <p className="text-xs text-slate-500 mt-1">{video.creatorName}</p>
                        <span className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                          <Eye className="h-3.5 w-3.5" />
                          {video.viewCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* User results */}
            {showUsers && (data?.users?.length ?? 0) > 0 && (
              <section>
                {activeTab === 'all' && (
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    {t('search.tabUsers')}
                  </h3>
                )}
                <div className="space-y-2">
                  {data!.users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-3 rounded-xl p-3 transition-colors hover:bg-white/60"
                      style={{
                        background: 'rgba(255,255,255,0.5)',
                        border: '1px solid rgba(148,163,184,0.15)',
                      }}
                    >
                      <UserAvatar
                        src={user.avatarUrl}
                        name={user.displayName}
                        size="md"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {user.displayName}
                        </p>
                        <p className="text-xs text-slate-500">@{user.handle}</p>
                      </div>
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {user.followerCount.toLocaleString()} {t('search.followers')}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Hashtag results */}
            {showHashtags && (data?.hashtags?.length ?? 0) > 0 && (
              <section>
                {activeTab === 'all' && (
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    {t('search.tabHashtags')}
                  </h3>
                )}
                <div className="flex flex-wrap gap-2">
                  {data!.hashtags.map((hashtag) => (
                    <div
                      key={hashtag.id}
                      className="flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors hover:bg-indigo-50"
                      style={{
                        background: 'rgba(255,255,255,0.7)',
                        border: '1px solid rgba(148,163,184,0.2)',
                      }}
                    >
                      <Hash className="h-4 w-4 text-indigo-500" />
                      <span className="font-medium text-slate-800">{hashtag.name}</span>
                      <span className="text-xs text-slate-400">
                        {hashtag.videoCount.toLocaleString()} {t('search.videos')}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
